import { cn } from "../../lib/utils";
import {
  IconAdjustmentsBolt,
  IconCloud,
  IconCurrencyDollar,
  IconEaseInOut,
  IconHeart,
  IconHelp,
  IconRouteAltLeft,
  IconTerminal2,
} from "@tabler/icons-react";
import React from "react";
import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'framer-motion';

export interface FeatureData {
  title: string;
  description: string;
  icon: React.ReactNode;
  key?: string;
}

interface FeaturesSectionWithHoverEffectsProps {
  limit?: number;
  displayInRow?: boolean;
  featuresData?: FeatureData[];
}

export function FeaturesSectionWithHoverEffects({
  limit,
  displayInRow = false,
  featuresData,
}: FeaturesSectionWithHoverEffectsProps) {
  const { t } = useTranslation();

  const defaultFeatures: FeatureData[] = [
    {
      title: "Built for developers",
      description:
        "Built for engineers, developers, dreamers, thinkers and doers.",
      icon: <IconTerminal2 />,
    },
    {
      title: "Ease of use",
      description:
        "It's as easy as using an Apple, and as expensive as buying one.",
      icon: <IconEaseInOut />,
    },
    {
      title: "Pricing like no other",
      description:
        "Our prices are best in the market. No cap, no lock, no credit card required.",
      icon: <IconCurrencyDollar />,
    },
    {
      title: "100% Uptime guarantee",
      description: "We just cannot be taken down by anyone.",
      icon: <IconCloud />,
    },
    {
      title: "Multi-tenant Architecture",
      description: "You can simply share passwords instead of buying new seats",
      icon: <IconRouteAltLeft />,
    },
    {
      title: "24/7 Customer Support",
      description:
        "We are available a 100% of the time. Atleast our AI Agents are.",
      icon: <IconHelp />,
    },
    {
      title: "Money back guarantee",
      description:
        "If you donot like EveryAI, we will convince you to like us.",
      icon: <IconAdjustmentsBolt />,
    },
    {
      title: "And everything else",
      description: "I just ran out of copy ideas. Accept my sincere apologies",
      icon: <IconHeart />,
    },
  ];

  const features = featuresData || defaultFeatures;

  const translatedFeatures = features.map(feature => {
    if (feature.key) {
      return {
        ...feature,
        title: t(feature.key + '.title', feature.title || ''),
        description: t(feature.key + '.description', feature.description || '')
      };
    }
    return feature;
  });

  

  const displayFeatures = limit ? translatedFeatures.slice(0, limit) : translatedFeatures;

  if (displayInRow) {
    return (
      <section className="tw-pt-[1rem] tw-pb-[4rem] max-[734px]:tw-pt-0 max-[734px]:tw-mb-[1em] max-[734px]:tw-pb-0">
        <div className="tw-container tw-mx-auto tw-pr-[1rem]">
          <div role="list" className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-3 tw-max-w-6xl tw-mx-auto">
            {displayFeatures.map((feature, index) => (
              <Feature key={feature.title} {...feature} index={index} displayInRow={true} />
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="tw-py-16">
      <div className="tw-container tw-mx-auto tw-px-4">
        <div role="list" className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-3 tw-max-w-7xl tw-mx-auto">
          {displayFeatures.map((feature, index) => (
            <Feature key={feature.title} {...feature} index={index} displayInRow={false} />
          ))}
        </div>
      </div>
    </section>
  );
}

const Feature = ({
  title,
  description,
  icon,
  index,
  displayInRow,
}: {
  title: string;
  description: string;
  icon: React.ReactNode;
  index: number;
  displayInRow?: boolean;
}) => {
  const prefersReducedMotion = useReducedMotion();

  return (
    <motion.div
      role="listitem"
      className={cn(
        "tw-group tw-relative tw-transition-all tw-duration-300",
        displayInRow ? "" : "lg:tw-border-r lg:tw-border-b-0 last:lg:tw-border-r-0 tw-border-b last:tw-border-b-0 tw-border-transparent"
      )}
      initial={prefersReducedMotion ? false : { opacity: 0, y: 20 }}
      animate={prefersReducedMotion ? undefined : { opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.08, ease: "easeOut" }}
    >
      <div className={cn(displayInRow ? "tw-pl-[2.5em] tw-pr-[1em] max-[734px]:tw-pr-[2em]" : "tw-px-6")}>
        <div className="tw-flex tw-items-center tw-gap-2">
          <div className="tw-flex-shrink-0">
            <div className="tw-text-blue-600 dark:tw-text-blue-400 tw-w-6 tw-h-6">{icon}</div>
          </div>
          <h3 className="tw-text-lg tw-font-semibold tw-text-gray-900 dark:tw-text-gray-100">
            {title}
          </h3>
        </div>

        <p className="feature-description">
          {description}
        </p>
      </div>
    </motion.div>
  );
};
